/**
 * Harness calibration figure — NOT game content.
 *
 * Exists so the asset workbench can be tested and so a sub-agent can eyeball the
 * conventions before building anything real. It deliberately demonstrates all of
 * them at once:
 *   - origin at the ground contact point (the base sits exactly on y=0)
 *   - +Z forward (the arrow points along +Z)
 *   - Y up (the post rises to exactly 1.000 m)
 *   - every material comes from the shared `surface()` seam
 *
 * If this renders correctly in the workbench, the workbench is working.
 */

import * as THREE from 'three';
import { surface } from './materials';
import type { HarnessAsset } from './types';

function build(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'calibration';

  // 1 m vertical post — read height at a glance.
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.04, 1.0, 0.04), surface('metal', 0xc8ccd2));
  post.position.set(0, 0.5, 0);
  post.castShadow = true;
  g.add(post);

  // Base plate, flush with the ground plane.
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.18, 0.02, 24),
    surface('mineral', 0x8b8f96),
  );
  base.position.set(0, 0.01, 0);
  base.receiveShadow = true;
  base.castShadow = true;
  g.add(base);

  // Forward arrow along +Z, so orientation mistakes are obvious immediately.
  const shaft = new THREE.Mesh(
    new THREE.BoxGeometry(0.03, 0.03, 0.3),
    surface('organic', 0xd98b7a),
  );
  shaft.position.set(0, 0.06, 0.17);
  g.add(shaft);

  const head = new THREE.Mesh(
    new THREE.ConeGeometry(0.06, 0.14, 16),
    surface('organic', 0xd98b7a),
  );
  head.position.set(0, 0.06, 0.39);
  head.rotation.x = Math.PI / 2;
  g.add(head);

  // A box at Rosalie's intended height, for scale comparison against real assets.
  const ref = new THREE.Mesh(
    new THREE.BoxGeometry(0.34, 0.32, 0.55),
    surface('fabric', 0xe08a76),
  );
  ref.position.set(0.5, 0.16, 0);
  ref.castShadow = true;
  g.add(ref);

  return g;
}

export const calibrationAsset: HarnessAsset = {
  id: 'calibration',
  label: 'Calibration figure',
  description: '1 m post, +Z arrow, and a Rosalie-sized block. Harness only — not game content.',
  footprint: { width: 0.9, height: 1.0, length: 0.6 },
  harnessOnly: true,
  create: build,
};
